import { Box, Typography, IconButton } from '@mui/material'
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material'
import PetAvatar from '../PetAvatar'
import { getAppointmentDuration } from '../../utils/appointmentUtils'
import { L, useLavMobile } from './appointmentLavenderFields'

/**
 * PetAppointmentCard - Displays a pet's appointment info in the overview
 * 
 * @param {object} appointment - The appointment data
 * @param {object} appointment.pet - Pet object with name, species, breed
 * @param {object} appointment.service - Service object with name, price, duration_minutes
 * @param {array} appointment.addOns - Array of add-on objects
 * @param {object} appointment.employee - Employee object with first_name, last_name
 * @param {function} onEdit - Callback when edit button is clicked
 * @param {function} onDelete - Callback when delete button is clicked
 * @param {boolean} showEdit - Whether to show edit button (default: true)
 * @param {boolean} showDelete - Whether to show delete button (default: false)
 */
export default function PetAppointmentCard({
	appointment,
	onEdit,
	onDelete,
	showEdit = true,
	showDelete = false,
	currencySymbol = '$'
}) {
	const { pet, service, addOns = [], employee } = appointment
	// Touch sizing (design `.mbap .ap-petcard`) inside the mobile drawer.
	const mobile = useLavMobile()

	// Calculate totals
	const servicePrice = service?.price || 0
	const addOnsPrice = addOns.reduce((sum, addon) => sum + (addon.price || 0), 0)
	const totalPrice = servicePrice + addOnsPrice

	// Use getAppointmentDuration to get the correct duration from timestamps (edit mode)
	// or from service + addOns (add mode)
	const totalDuration = getAppointmentDuration(appointment)

	const employeeName = employee
		? `${employee.first_name || ''} ${employee.last_name || ''}`.trim()
		: 'Not assigned'

	const formatCurrency = (cents) => `${currencySymbol}${(cents / 100).toFixed(2)}`

	// Build pet info string
	const petInfo = [pet?.breed, pet?.species].filter(Boolean).join(' | ')

	return (
		<Box sx={{
			display: 'flex',
			flexDirection: 'column',
			gap: '9px',
			padding: mobile ? '13px' : '12px',
			bgcolor: L.field,
			border: `1px solid ${L.border}`,
			borderRadius: mobile ? '15px' : L.r,
			fontFamily: L.font,
		}}>
			{/* Head: avatar + name/breed + actions */}
			<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
				<PetAvatar petId={pet?.id} size={mobile ? 40 : 36} editable={false} />

				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography sx={{
						fontFamily: L.font, fontWeight: 750, fontSize: mobile ? '16px' : '15px', color: L.text,
						overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
					}}>
						{pet?.name || 'Pet'}
					</Typography>
					{petInfo && (
						<Typography sx={{
							fontFamily: L.font, fontSize: mobile ? '12.5px' : '12px', color: L.muted, mt: '1px',
							overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
						}}>
							{petInfo}
						</Typography>
					)}
				</Box>

				<Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
					{showDelete && onDelete && (
						<IconButton onClick={onDelete} size="small" sx={{ ...iconBtnSx, ...(mobile ? iconBtnMobileSx : {}) }}>
							<DeleteIcon sx={{ fontSize: 16 }} />
						</IconButton>
					)}
					{showEdit && onEdit && (
						<IconButton onClick={onEdit} size="small" sx={{ ...iconBtnSx, ...(mobile ? iconBtnMobileSx : {}) }}>
							<EditIcon sx={{ fontSize: 16 }} />
						</IconButton>
					)}
				</Box>
			</Box>

			{/* Service block */}
			<Box sx={{
				bgcolor: L.surface, borderRadius: mobile ? '11px' : L.rSm, p: mobile ? '10px 12px' : '9px 11px',
				fontSize: mobile ? '13.5px' : '13px', fontWeight: 650, color: L.text, fontFamily: L.font,
			}}>
				<Box>{service?.name || 'No service selected'}</Box>
				{/* One add-on per line (design `.ap-petcard-adds`). */}
				{addOns.map((a, i) => (
					<Box key={a.id ?? i} sx={{ color: L.muted, fontWeight: 550, fontSize: '12.5px', mt: '3px' }}>
						+ {a.name}
					</Box>
				))}
			</Box>

			{/* Meta line */}
			<Typography sx={{ fontSize: mobile ? '13px' : '12.5px', color: L.muted, fontWeight: 600, fontFamily: L.font }}>
				{formatCurrency(totalPrice)} · {totalDuration}mins · {employeeName}
			</Typography>
		</Box>
	)
}

const iconBtnSx = {
	width: 28, height: 28, p: 0, borderRadius: '8px', color: L.muted,
	'&:hover': { bgcolor: L.surface, color: L.text },
}

const iconBtnMobileSx = { width: 34, height: 34, borderRadius: '10px' }

